import type { DateFormat, DecimalSeparator, ThousandSeparator } from "../regional-format";

/**
 * QL-349 — the tenant's reporting currency: what a Cash filter's threshold
 * and total are read in, and what every synced account balance is converted
 * into. Allowlisted to the currencies with seeded FX data (the base
 * currencies `currencyBackfill.ts` keeps stocked, plus USD — the pivot every
 * rate is already quoted against, so it needs no conversion of its own).
 */
export const TENANT_CURRENCIES = ["NOK", "EUR", "SEK", "GBP", "USD"] as const;
export type TenantCurrency = (typeof TENANT_CURRENCIES)[number];

export function isTenantCurrency(value: string): value is TenantCurrency {
  return (TENANT_CURRENCIES as readonly string[]).includes(value);
}

/**
 * GET/PATCH /internal/tenant/regional — the tenant's regional settings:
 * the two language fields, the three display-format fields (QL-108), the
 * reporting currency (QL-349) and the time zone (QL-689). One endpoint, one
 * Save.
 */
export interface TenantRegionalSettings {
  defaultLanguage: string;
  supportedLanguages: string[];
  dateFormat: DateFormat;
  decimalSeparator: DecimalSeparator;
  thousandSeparator: ThousandSeparator;
  currency: TenantCurrency;
  /** IANA time zone the tenant's PDFs are dated in (QL-689); default Europe/Oslo. */
  timeZone: string;
}

/**
 * PATCH body — the full settings object (single Save on the settings page).
 * `timeZone` is the one optional field: omitting it keeps the tenant's
 * currently stored zone, so a client built before the field existed (sending
 * only the six earlier fields) doesn't get a 400. A present but invalid zone
 * is still a 400.
 */
export type TenantRegionalSettingsUpdateRequest = Omit<TenantRegionalSettings, "timeZone"> & {
  timeZone?: string;
};
